/**
 * Equipment and infrastructure: pure state and schedule math shared by the facet reducers, the
 * server (Today, routes, agent) and the web. No DOM, no Worker APIs.
 *
 * Meters: a reading is only comparable with readings of the same meter (an "epoch"). A new or
 * reset meter starts a new epoch, so a lower number is never treated as negative use.
 * Schedules: intervals live on the asset (attrs["operations.schedule"]); the last actual service of
 * each type is the baseline. A date passing never marks work done; a projected date is labeled
 * estimated.
 * Problems: a problem stays open until a repair that names it is recorded as fixed, or, for a
 * checklist finding, until a later check of the same item passes.
 */
import { addDays, daysBetween, type Badge, type FacetEvent, type Quantity, type SubjectRef } from "../../../contract";
import { msg } from "../../../contract";

// ---------------------------------------------------------------- vocabularies (values are stable API identifiers)

export const SERVICE_TYPES = {
  oil: "Engine oil and filter",
  grease: "Grease",
  filters: "Air and fuel filters",
  hydraulic: "Hydraulic oil and filter",
  transmission: "Transmission and gear oil",
  coolant: "Coolant",
  tires: "Tires",
  brakes: "Brakes",
  belts: "Belts and hoses",
  battery: "Battery",
  inspection: "Safety inspection",
  general: "General service",
  other: "Other service",
} as const;
export type ServiceType = keyof typeof SERVICE_TYPES;

/** Today and alert titles per service type, so the service name is translated with the sentence. */
export const SERVICE_OVERDUE: Record<ServiceType, string> = {
  oil: "{{asset}}: oil change overdue", grease: "{{asset}}: greasing overdue", filters: "{{asset}}: filters overdue",
  hydraulic: "{{asset}}: hydraulic service overdue", transmission: "{{asset}}: transmission service overdue", coolant: "{{asset}}: coolant service overdue",
  tires: "{{asset}}: tire service overdue", brakes: "{{asset}}: brake service overdue", belts: "{{asset}}: belts and hoses overdue",
  battery: "{{asset}}: battery service overdue", inspection: "{{asset}}: safety inspection overdue", general: "{{asset}}: service overdue", other: "{{asset}}: service overdue",
};
export const SERVICE_DUE: Record<ServiceType, string> = {
  oil: "{{asset}}: oil change due", grease: "{{asset}}: greasing due", filters: "{{asset}}: filters due",
  hydraulic: "{{asset}}: hydraulic service due", transmission: "{{asset}}: transmission service due", coolant: "{{asset}}: coolant service due",
  tires: "{{asset}}: tire service due", brakes: "{{asset}}: brake service due", belts: "{{asset}}: belts and hoses due",
  battery: "{{asset}}: battery service due", inspection: "{{asset}}: safety inspection due", general: "{{asset}}: service due", other: "{{asset}}: service due",
};

export const METER_UNITS = { hours: "Hours", miles: "Miles", km: "Kilometers" } as const;
export type MeterUnit = keyof typeof METER_UNITS;
/** Unit registry keys for display (UNIT_LABELS): h, mi, km. */
export const METER_SYMBOL: Record<MeterUnit, string> = { hours: "h", miles: "mi", km: "km" };

export const ISSUE_KINDS = {
  fence: "Fence", gate: "Gate", water: "Water", windmill: "Windmill", pens: "Pens and corrals", building: "Building",
  road: "Road", power: "Power", equipment: "Equipment", other: "Other",
} as const;
export type IssueKind = keyof typeof ISSUE_KINDS;

export const SEVERITY = { urgent: "Needs fixing now", soon: "Fix soon", minor: "When there's time" } as const;
export type Severity = keyof typeof SEVERITY;

export const REPAIR_OUTCOMES = { fixed: "Fixed", temporary: "Patched for now", not_fixed: "Still not fixed" } as const;
export type RepairOutcome = keyof typeof REPAIR_OUTCOMES;

export const CHECK_RESULTS = { ok: "OK", attention: "Needs attention", failed: "Failed", na: "Not checked" } as const;
export type CheckResult = keyof typeof CHECK_RESULTS;

export const FUEL_TYPES = { diesel: "Diesel", gasoline: "Gasoline", def: "DEF", other: "Other fuel" } as const;
export type FuelType = keyof typeof FUEL_TYPES;

// ---------------------------------------------------------------- event payloads

export interface PartUse { part: string; part_number?: string; quantity?: number; unit?: string; stock_item?: string }
export interface MeterFields { meter?: number; meter_unit?: MeterUnit }
export interface ServiceData extends MeterFields {
  service_type: ServiceType; work?: string; done_by?: string; shop?: string; parts?: PartUse[]; receipt?: string;
  next_due_on?: string; next_due_meter?: number; notes?: string;
}
export interface RepairData extends MeterFields {
  work: string; outcome: RepairOutcome; issue_id?: string; problem?: string; out_of_service?: boolean; parts?: PartUse[];
  done_by?: string; shop?: string; receipt?: string; notes?: string;
}
export interface IssueData {
  problem: string; kind?: IssueKind; severity?: Severity; out_of_service?: boolean; location?: string; parts_needed?: string; notes?: string;
}
export interface MeterData { reading: number; meter_unit?: MeterUnit; new_meter?: boolean }
export interface FuelData extends MeterFields { amount: Quantity; fuel_type?: FuelType; receipt?: string; notes?: string }
export interface CheckItem { item: string; result: CheckResult; note?: string }
export interface InspectionData extends MeterFields { checklist?: string; items: CheckItem[]; notes?: string }

/** One service interval, stored on the asset under attrs["operations.schedule"]. */
export interface ScheduleInterval { service_type: ServiceType; every_meter?: number; every_months?: number; note?: string }

export const EQUIPMENT_TYPES = ["operations.service", "operations.repair", "operations.issue", "operations.meter", "operations.fuel", "operations.inspection"];
export const PROBLEM_TYPES = ["operations.issue", "operations.repair", "operations.inspection"];

// ---------------------------------------------------------------- dates

/** Add calendar months, clamping to the month's last day (Jan 31 + 1 month = Feb 28 or 29). */
export function addMonths(date: string, months: number): string {
  const [y, m, d] = date.slice(0, 10).split("-").map(Number);
  const total = y * 12 + (m - 1) + months;
  const year = Math.floor(total / 12);
  const month = total - year * 12;
  const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return `${String(year).padStart(4, "0")}-${String(month + 1).padStart(2, "0")}-${String(Math.min(d, last)).padStart(2, "0")}`;
}

const dayOf = (e: { occurred_at: string | null }) => (e.occurred_at ? e.occurred_at.slice(0, 10) : null);

// ---------------------------------------------------------------- meters

export interface Reading { value: number; on: string; unit?: MeterUnit; event_id: string; type: string; reset?: boolean; epoch: number }

export interface MeterEvent { id: string; type: string; occurred_at: string | null; data: Record<string, unknown> }

/** Every dated meter reading in a set of events, oldest first, with meter epochs numbered. */
export function readingsFrom(events: MeterEvent[]): Reading[] {
  const raw: Array<Omit<Reading, "epoch">> = [];
  for (const e of events) {
    const on = dayOf(e);
    if (!on) continue;
    const d = e.data;
    const value = e.type === "operations.meter" ? d.reading : d.meter;
    if (typeof value !== "number" || !Number.isFinite(value)) continue;
    raw.push({ value, on, unit: d.meter_unit as MeterUnit | undefined, event_id: e.id, type: e.type, reset: e.type === "operations.meter" && d.new_meter === true });
  }
  // Stable sort by date; a reset sorts before other readings on the same day so it opens the epoch.
  const sorted = raw.map((r, i) => ({ r, i })).sort((a, b) => a.r.on.localeCompare(b.r.on) || Number(!!b.r.reset) - Number(!!a.r.reset) || a.i - b.i).map((x) => x.r);
  let epoch = 0;
  return sorted.map((r, i) => {
    if (r.reset && i > 0) epoch++;
    return { ...r, epoch };
  });
}

/** The meter epoch a date falls in. */
export function epochOn(readings: Reading[], on: string): number {
  let epoch = 0;
  for (const r of readings) if (r.reset && r.on <= on) epoch = r.epoch;
  return epoch;
}

/**
 * A new reading that contradicts the meter's chronology: an earlier date with a higher reading, or a
 * later date with a lower one, on the same meter. Pass the asset's other events (leave out the one
 * being corrected). Returns the reading it conflicts with.
 */
export function meterConflict(events: MeterEvent[], next: { value: number; on: string; reset?: boolean }): Reading | null {
  if (next.reset) return null;
  const readings = readingsFrom(events);
  const epoch = epochOn(readings, next.on);
  const same = readings.filter((r) => r.epoch === epoch);
  const before = same.filter((r) => r.on < next.on && r.value > next.value).sort((a, b) => b.value - a.value)[0];
  if (before) return before;
  return same.filter((r) => r.on > next.on && r.value < next.value).sort((a, b) => a.value - b.value)[0] ?? null;
}

/** Average use per day on the current meter over up to a year of readings. Null when it can't be told. */
export function usagePerDay(readings: Reading[]): number | null {
  if (!readings.length) return null;
  const epoch = readings[readings.length - 1].epoch;
  const current = readings.filter((r) => r.epoch === epoch);
  const last = current[current.length - 1];
  const window = current.filter((r) => daysBetween(r.on, last.on) <= 365);
  const first = window[0];
  const days = daysBetween(first.on, last.on);
  if (window.length < 2 || days < 7 || last.value <= first.value) return null;
  return (last.value - first.value) / days;
}

// ---------------------------------------------------------------- problems

export interface ProblemRef { asset_ids: string[]; place_ids: string[] }

export interface OpenProblem extends ProblemRef {
  /** Stable key: the reporting event's root id, or `<root>:<item index>` for a checklist finding. */
  id: string;
  event_id: string;
  source: "issue" | "repair" | "inspection";
  problem: string;
  kind?: IssueKind;
  severity?: Severity;
  out_of_service: boolean;
  since: string | null;
  location?: string;
  parts_needed?: string;
  /** The checklist item, for findings from an inspection. */
  check_item?: string;
  last_work?: { on: string | null; work: string; outcome: RepairOutcome; event_id: string };
}

export interface FixedProblem extends OpenProblem { fixed_on: string | null; fixed_by_event: string; fix: string }

interface LedgerEvent { id: string; root_id?: string; type: string; occurred_at: string | null; data: Record<string, unknown>; subjects: SubjectRef[] }

const norm = (s: string) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/\s+/g, " ").trim();
const refsOf = (subjects: SubjectRef[]): ProblemRef => ({
  asset_ids: subjects.filter((s) => s.kind === "asset").map((s) => s.id),
  place_ids: subjects.filter((s) => s.kind === "place").map((s) => s.id),
});
const shares = (a: ProblemRef, b: ProblemRef) => a.asset_ids.some((id) => b.asset_ids.includes(id)) || a.place_ids.some((id) => b.place_ids.includes(id));

/** Open and fixed problems from issue, repair and checklist events, processed oldest first. */
export function problemLedger(events: LedgerEvent[], keepFixed = 20): { open: OpenProblem[]; fixed: FixedProblem[] } {
  const open = new Map<string, OpenProblem>();
  const fixed: FixedProblem[] = [];
  const close = (key: string, on: string | null, eventId: string, fix: string) => {
    const p = open.get(key);
    if (!p) return;
    open.delete(key);
    fixed.push({ ...p, fixed_on: on, fixed_by_event: eventId, fix });
  };
  for (const e of events) {
    const root = e.root_id ?? e.id;
    const on = dayOf(e);
    const where = refsOf(e.subjects);
    if (e.type === "operations.issue") {
      const d = e.data as unknown as IssueData;
      open.set(root, {
        id: root, event_id: e.id, source: "issue", problem: d.problem, kind: d.kind, severity: d.severity, out_of_service: d.out_of_service === true,
        since: on, location: d.location, parts_needed: d.parts_needed, ...where,
      });
    } else if (e.type === "operations.repair") {
      const d = e.data as unknown as RepairData;
      const linked = d.issue_id ? open.get(d.issue_id) : undefined;
      if (linked) {
        if (d.outcome === "fixed") close(linked.id, on, e.id, d.work);
        else open.set(linked.id, { ...linked, out_of_service: d.out_of_service ?? linked.out_of_service, last_work: { on, work: d.work, outcome: d.outcome, event_id: e.id } });
      } else if (!d.issue_id && d.outcome !== "fixed") {
        open.set(root, {
          id: root, event_id: e.id, source: "repair", problem: d.problem ?? d.work, out_of_service: d.out_of_service === true, since: on, ...where,
          last_work: { on, work: d.work, outcome: d.outcome, event_id: e.id },
        });
      }
    } else if (e.type === "operations.inspection") {
      const d = e.data as unknown as InspectionData;
      (d.items ?? []).forEach((item, i) => {
        if (item.result === "ok") {
          for (const p of [...open.values()]) {
            if (p.source === "inspection" && p.check_item && norm(p.check_item) === norm(item.item) && shares(p, where)) close(p.id, on, e.id, item.note ?? item.item);
          }
        } else if (item.result === "attention" || item.result === "failed") {
          const key = `${root}:${i}`;
          open.set(key, {
            id: key, event_id: e.id, source: "inspection", problem: item.note ? `${item.item}: ${item.note}` : item.item, check_item: item.item,
            severity: item.result === "failed" ? "urgent" : "soon", out_of_service: false, since: on, ...where,
          });
        }
      });
    }
  }
  const byDate = (a: { since: string | null }, b: { since: string | null }) => (a.since ?? "").localeCompare(b.since ?? "");
  return { open: [...open.values()].sort(byDate), fixed: fixed.sort((a, b) => (b.fixed_on ?? "").localeCompare(a.fixed_on ?? "")).slice(0, keepFixed) };
}

/** Whole days a problem has been open. */
export function openDays(p: { since: string | null }, today: string): number | null {
  return p.since ? Math.max(0, daysBetween(p.since, today)) : null;
}

// ---------------------------------------------------------------- the asset facet

export interface ServiceRecord {
  event_id: string; root_id: string; on: string; service_type: ServiceType; meter?: number; epoch: number; work?: string;
  next_due_on?: string; next_due_meter?: number;
}

export interface EquipmentState {
  /** Latest reading on the current meter. */
  meter: Reading | null;
  /** Recent readings on the current meter, oldest first (for use rates and chronology). */
  readings: Reading[];
  /** Day the current meter started, when it was replaced or reset. */
  meter_since?: string;
  /** Last actual service of each type. */
  services: Partial<Record<ServiceType, ServiceRecord>>;
  last_work?: { on: string; type: string; event_id: string };
  open: OpenProblem[];
  fixed: FixedProblem[];
  last_fuel?: { on: string; amount: Quantity; fuel_type?: FuelType; event_id: string };
  last_inspection?: { on: string; checklist?: string; ok: number; flagged: number; event_id: string };
  counts: { services: number; repairs: number; fuel: number; inspections: number };
}

export function reduceEquipment(events: FacetEvent[]): EquipmentState | null {
  if (!events.length) return null;
  const all = readingsFrom(events);
  const services: EquipmentState["services"] = {};
  const counts = { services: 0, repairs: 0, fuel: 0, inspections: 0 };
  let lastWork: EquipmentState["last_work"];
  let lastFuel: EquipmentState["last_fuel"];
  let lastInspection: EquipmentState["last_inspection"];
  for (const e of events) {
    const on = dayOf(e);
    if (e.type === "operations.service") {
      counts.services++;
      const d = e.data as ServiceData;
      if (on) {
        const prev = services[d.service_type];
        if (!prev || prev.on <= on) {
          services[d.service_type] = {
            event_id: e.id, root_id: e.root_id ?? e.id, on, service_type: d.service_type, meter: d.meter, epoch: epochOn(all, on), work: d.work,
            next_due_on: d.next_due_on, next_due_meter: d.next_due_meter,
          };
        }
      }
    }
    if (e.type === "operations.repair") counts.repairs++;
    if (e.type === "operations.fuel") {
      counts.fuel++;
      const d = e.data as FuelData;
      if (on && (!lastFuel || lastFuel.on <= on)) lastFuel = { on, amount: d.amount, fuel_type: d.fuel_type, event_id: e.id };
    }
    if (e.type === "operations.inspection") {
      counts.inspections++;
      const d = e.data as InspectionData;
      if (on && (!lastInspection || lastInspection.on <= on)) {
        lastInspection = { on, checklist: d.checklist, ok: (d.items ?? []).filter((i) => i.result === "ok").length, flagged: (d.items ?? []).filter((i) => i.result === "attention" || i.result === "failed").length, event_id: e.id };
      }
    }
    if ((e.type === "operations.service" || e.type === "operations.repair") && on && (!lastWork || lastWork.on <= on)) lastWork = { on, type: e.type, event_id: e.id };
  }
  const epoch = all.length ? all[all.length - 1].epoch : 0;
  const current = all.filter((r) => r.epoch === epoch);
  const { open, fixed } = problemLedger(events, 10);
  const resetAt = current.find((r) => r.reset)?.on;
  return {
    meter: current[current.length - 1] ?? null,
    readings: current.slice(-40),
    ...(resetAt ? { meter_since: resetAt } : {}),
    services, ...(lastWork ? { last_work: lastWork } : {}), open, fixed,
    ...(lastFuel ? { last_fuel: lastFuel } : {}), ...(lastInspection ? { last_inspection: lastInspection } : {}), counts,
  };
}

/** Place facet: problems reported at a place. */
export interface PlaceProblemsState { open: OpenProblem[]; fixed: FixedProblem[]; last_inspection?: { on: string; checklist?: string; flagged: number } }

export function reducePlaceProblems(events: FacetEvent[]): PlaceProblemsState | null {
  if (!events.length) return null;
  const { open, fixed } = problemLedger(events, 10);
  let last: PlaceProblemsState["last_inspection"];
  for (const e of events) {
    const on = dayOf(e);
    if (e.type !== "operations.inspection" || !on) continue;
    const items = ((e.data as InspectionData).items ?? []);
    if (!last || last.on <= on) last = { on, checklist: (e.data as InspectionData).checklist, flagged: items.filter((i) => i.result === "attention" || i.result === "failed").length };
  }
  if (!open.length && !fixed.length && !last) return null;
  return { open, fixed, ...(last ? { last_inspection: last } : {}) };
}

// ---------------------------------------------------------------- schedules

export type DueStatus = "overdue" | "due_soon" | "ok" | "no_record";

export interface ScheduleStatus {
  service_type: ServiceType;
  every_meter?: number;
  every_months?: number;
  note?: string;
  last?: { on: string; meter?: number; event_id: string };
  due_meter?: number;
  due_on?: string;
  /** Estimated day the meter reaches due_meter at the recent rate of use. */
  projected_on?: string;
  meter_left?: number;
  days_left?: number;
  status: DueStatus;
  /** What makes it due: the meter or the calendar. */
  reason?: "meter" | "date";
  /** The earlier of the due date and the projected date. */
  next_on?: string;
  /** next_on is a projection from meter use, not a set date. */
  estimated: boolean;
  /** The due point was stated on the last service record rather than calculated. */
  stated: boolean;
  /** The meter was replaced since the last service, so hours or miles since then are unknown. */
  meter_changed: boolean;
}

/** Days ahead that count as "due soon" for dates and projections. */
export const DUE_SOON_DAYS = 21;
/** Share of a meter interval left that counts as "due soon". */
export const DUE_SOON_SHARE = 0.1;

export function scheduleStatus(state: EquipmentState | null, schedule: ScheduleInterval[], today: string): ScheduleStatus[] {
  const rate = state ? usagePerDay(state.readings) : null;
  const meter = state?.meter ?? null;
  const out = schedule.map((interval): ScheduleStatus => {
    const base: ScheduleStatus = {
      service_type: interval.service_type, every_meter: interval.every_meter, every_months: interval.every_months, note: interval.note,
      status: "no_record", estimated: false, stated: false, meter_changed: false,
    };
    const last = state?.services[interval.service_type];
    if (!last) return base;
    base.last = { on: last.on, meter: last.meter, event_id: last.event_id };
    const meterComparable = meter !== null && last.meter !== undefined && last.epoch === meter.epoch;
    base.meter_changed = meter !== null && last.meter !== undefined && last.epoch !== meter.epoch;
    if (last.next_due_meter !== undefined) { base.due_meter = last.next_due_meter; base.stated = true; }
    else if (interval.every_meter && last.meter !== undefined) base.due_meter = last.meter + interval.every_meter;
    if (last.next_due_on) { base.due_on = last.next_due_on; base.stated = true; }
    else if (interval.every_months) base.due_on = addMonths(last.on, interval.every_months);

    let meterOver = false;
    let meterSoon = false;
    if (base.due_meter !== undefined && meterComparable && meter) {
      base.meter_left = base.due_meter - meter.value;
      meterOver = base.meter_left <= 0;
      const span = interval.every_meter ?? (last.meter !== undefined ? base.due_meter - last.meter : 0);
      meterSoon = !meterOver && span > 0 && base.meter_left <= span * DUE_SOON_SHARE;
      if (!meterOver && rate) base.projected_on = addDays(meter.on, Math.ceil(base.meter_left / rate));
    }
    let dateOver = false;
    let dateSoon = false;
    if (base.due_on) {
      base.days_left = daysBetween(today, base.due_on);
      dateOver = base.days_left < 0;
      dateSoon = !dateOver && base.days_left <= DUE_SOON_DAYS;
    }
    const projectedSoon = !!base.projected_on && daysBetween(today, base.projected_on) <= DUE_SOON_DAYS;
    const candidates = [base.due_on, base.projected_on].filter((d): d is string => !!d).sort();
    base.next_on = candidates[0];
    base.estimated = !!base.projected_on && base.next_on === base.projected_on && base.projected_on !== base.due_on;
    if (meterOver || dateOver) {
      base.status = "overdue";
      base.reason = meterOver ? "meter" : "date";
    } else if (meterSoon || dateSoon || projectedSoon) {
      base.status = "due_soon";
      base.reason = meterSoon || (projectedSoon && !dateSoon) ? "meter" : "date";
    } else base.status = "ok";
    return base;
  });
  const rank: Record<DueStatus, number> = { overdue: 0, due_soon: 1, ok: 2, no_record: 3 };
  return out.sort((a, b) => rank[a.status] - rank[b.status] || (a.next_on ?? "9999").localeCompare(b.next_on ?? "9999"));
}

// ---------------------------------------------------------------- one asset's whole picture

export type AssetCondition = "broken" | "overdue" | "due_soon" | "ok";

export interface AssetRowLike { id: string; name: string; kind: string; make: string | null; model: string | null; meter_unit: string | null; attrs: Record<string, unknown> }

export interface AssetStatus {
  id: string;
  name: string;
  kind: string;
  make: string | null;
  model: string | null;
  meter_unit: MeterUnit | null;
  meter: { value: number; on: string } | null;
  usage_per_day: number | null;
  schedule: ScheduleStatus[];
  open: OpenProblem[];
  condition: AssetCondition;
  badges: Badge[];
  last_work?: EquipmentState["last_work"];
  last_fuel?: EquipmentState["last_fuel"];
}

export function scheduleOf(attrs: Record<string, unknown> | null | undefined): ScheduleInterval[] {
  const raw = attrs?.["operations.schedule"];
  return Array.isArray(raw) ? (raw as ScheduleInterval[]).filter((x) => x && typeof x.service_type === "string") : [];
}

export function assetStatus(asset: AssetRowLike, state: EquipmentState | null, today: string): AssetStatus {
  const schedule = scheduleStatus(state, scheduleOf(asset.attrs), today);
  const open = state?.open ?? [];
  const broken = open.some((p) => p.out_of_service);
  const condition: AssetCondition = broken ? "broken" : schedule.some((s) => s.status === "overdue") ? "overdue" : schedule.some((s) => s.status === "due_soon") ? "due_soon" : "ok";
  const meterUnit = (asset.meter_unit as MeterUnit | null) ?? state?.meter?.unit ?? null;
  return {
    id: asset.id, name: asset.name, kind: asset.kind, make: asset.make, model: asset.model, meter_unit: meterUnit,
    meter: state?.meter ? { value: state.meter.value, on: state.meter.on } : null,
    usage_per_day: state ? usagePerDay(state.readings) : null,
    schedule, open, condition, badges: assetBadges(condition, open.length),
    ...(state?.last_work ? { last_work: state.last_work } : {}), ...(state?.last_fuel ? { last_fuel: state.last_fuel } : {}),
  };
}

export function assetBadges(condition: AssetCondition, openCount: number): Badge[] {
  const out: Badge[] = [];
  if (condition === "broken") out.push({ label: msg("Broken"), tone: "bad", icon: "warn" });
  if (condition === "overdue") out.push({ label: msg("Service overdue"), tone: "bad", icon: "wrench" });
  if (condition === "due_soon") out.push({ label: msg("Service due"), tone: "warn", icon: "wrench" });
  if (condition !== "broken" && openCount > 0) out.push({ label: openCount === 1 ? msg("1 open problem") : msg("{{count}} open problems", { count: openCount }), tone: "warn", icon: "warn" });
  return out;
}

/** The asset's meter unit, or a unit stated on a reading when the asset has none set. */
export function meterUnitOf(asset: { meter_unit: string | null } | null | undefined, state?: EquipmentState | null): MeterUnit | null {
  return (asset?.meter_unit as MeterUnit | null) ?? state?.meter?.unit ?? null;
}
